'use client'

import Image from 'next/image'
import { Check } from '@/components/icons/glyphs'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

const SCAN_MS = 2000
// The list settles a beat after the line leaves the photo, then offers to add everything.
const DONE_MS = SCAN_MS + 400

// What Haven finds in the photo, in the order the scan line reaches each one.
// Boxes are percentages of the photo (x, y, width, height); `at` is where the
// line is when it reaches the box's middle.
const FOUND = [
  { n: 1, name: 'Range hood', box: [49.5, 15.5, 23, 23.5] },
  { n: 2, name: 'Pull-down faucet', box: [11, 45, 8, 14] },
  { n: 3, name: 'LG microwave', box: [20, 49.5, 14.5, 10.5] },
  { n: 4, name: 'Gas range', box: [48, 58, 26.5, 32] },
  { n: 5, name: 'Dishwasher', box: [18, 70, 7.5, 29.5] },
].map((f) => ({ ...f, at: (f.box[1] + f.box[3] / 2) / 100 }))

function FoundList({ elapsed, finished, className }: { elapsed: number; finished: boolean; className?: string }) {
  return (
    <div aria-hidden className={cn('rounded-2xl border border-border bg-card p-4 shadow-lg shadow-foreground/10', className)}>
      <div className="flex items-baseline justify-between">
        <p className="font-medium text-foreground">Found in Kitchen</p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {FOUND.filter((f) => elapsed >= f.at * SCAN_MS).length} of {FOUND.length}
        </p>
      </div>
      <ul className="mt-3 space-y-2 text-sm">
        {FOUND.map((f) => {
          const shown = elapsed >= f.at * SCAN_MS
          return (
            <li
              key={f.n}
              className={cn(
                'flex items-center gap-3 transition-[opacity,translate] duration-300 ease-out',
                shown ? 'translate-x-0 opacity-100' : '-translate-x-1 opacity-0'
              )}
            >
              <span className="flex size-5 items-center justify-center rounded-md bg-primary/15 text-xs font-semibold text-primary tabular-nums">
                {f.n}
              </span>
              <span className="flex-1 text-foreground">{f.name}</span>
              <Check className="size-4 text-primary" />
            </li>
          )
        })}
      </ul>
      <div
        className={cn(
          'mt-4 rounded-full py-1.5 text-center text-sm transition-colors duration-500',
          finished ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        )}
      >
        Add all {FOUND.length} to Kitchen
      </div>
    </div>
  )
}

/**
 * One photo becomes an inventory: a scan passes over the kitchen and each
 * appliance joins the list as the line reaches it. Plays once. The list sits
 * under the section heading on desktop, so it never covers the boxes.
 */
export function DetectionScan({ heading }: { heading: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.5 })
  const reduceMotion = useReducedMotion()
  const [elapsed, setElapsed] = useState(0)
  const finished = elapsed >= DONE_MS

  useEffect(() => {
    if (!inView) return
    if (reduceMotion) {
      setElapsed(DONE_MS)
      return
    }
    const timers = [...FOUND.map((f) => f.at * SCAN_MS), SCAN_MS, DONE_MS].map((t) =>
      window.setTimeout(() => setElapsed(t), t)
    )
    return () => timers.forEach(window.clearTimeout)
  }, [inView, reduceMotion])

  const scanning = inView && !reduceMotion

  return (
    <div className="grid items-start gap-10 md:grid-cols-12 md:gap-12">
      <div className="md:col-span-5">
        {heading}
        <FoundList elapsed={elapsed} finished={finished} className="mt-10 hidden max-w-sm md:block" />
      </div>
      <div ref={ref} className="md:col-span-7">
        <div className="relative aspect-[1600/1067] overflow-hidden rounded-2xl border border-border">
          <Image
            src="/landing/item-detection/kitchen.webp"
            alt="A kitchen with a range hood, gas range, microwave, dishwasher, and pull-down faucet, each outlined as Haven finds it"
            fill
            sizes="(min-width: 768px) 640px, 100vw"
            className="object-cover"
          />
          {/* Not yet scanned: muted. The cover shrinks toward the bottom as the line passes (transform only). */}
          <motion.div
            aria-hidden
            className="absolute inset-0 origin-bottom bg-background/40 backdrop-grayscale"
            initial={{ scaleY: 1 }}
            animate={{ scaleY: scanning || reduceMotion ? 0 : 1 }}
            transition={{ duration: reduceMotion ? 0 : SCAN_MS / 1000, ease: 'linear' }}
          />
          {FOUND.map((f) => {
            const [x, y, w, h] = f.box
            const shown = elapsed >= f.at * SCAN_MS
            return (
              <div
                key={f.n}
                aria-hidden
                className={cn(
                  'absolute rounded-md border-2 border-primary bg-primary/10 transition-[opacity,scale] duration-300 ease-out',
                  shown ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                )}
                style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
              >
                <span className="absolute -left-0.5 -top-6 rounded-md bg-primary px-1.5 text-xs font-semibold leading-5 text-primary-foreground tabular-nums">
                  {f.n}
                </span>
              </div>
            )
          })}
          {/* The line sits at the top of a full-height track; translating the track by 100% sweeps it down */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            initial={{ y: '0%', opacity: 0 }}
            animate={scanning ? { y: '100%', opacity: [0, 1, 1, 0] } : { opacity: 0 }}
            transition={{ duration: SCAN_MS / 1000, ease: 'linear', opacity: { times: [0, 0.05, 0.9, 1], duration: SCAN_MS / 1000 } }}
          >
            <div className="h-px bg-primary shadow-[0_0_24px_6px_color-mix(in_oklab,var(--color-primary)_55%,transparent)]" />
          </motion.div>
        </div>
        <FoundList elapsed={elapsed} finished={finished} className="mt-4 md:hidden" />
      </div>
    </div>
  )
}
